import React, { useState, useEffect } from 'react';
import { 
  Button, Form, Input, message, Space, Card 
} from 'antd';
import { EditOutlined, SaveOutlined } from '@ant-design/icons';
import API_ENDPOINTS from '../config/api';

const { TextArea } = Input;

const WebSettings = () => {
  const [settings, setSettings] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.WEBSITESETTINGS);
      const result = await response.json();
      if (result.status === 'success' && result.data.length > 0) {
        const firstSetting = result.data[0]; // Assuming we want the first record
        setSettings(firstSetting);
        form.setFieldsValue(firstSetting);
      }
    } catch (error) {
      message.error('Failed to fetch settings');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Append all settings fields
      Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      });

      formData.append('_method', 'PUT');
      formData.append('id', settings.id.toString());

      const url = `${API_ENDPOINTS.WEBSITESETTINGS}/${settings.id}`;

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update settings');
      }
      
      if (result.status === 'success') {
        message.success('Settings updated successfully');
        setSettings(result.data);
        setIsEditing(false);
        fetchSettings();
      } else {
        throw new Error(result.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      message.error(error.message || 'Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <h1>Website Settings</h1>
        {!isEditing && (
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={handleEdit}
          >
            Edit Settings
          </Button>
        )}
      </div>

      <Card>
        {settings ? (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            disabled={!isEditing}
          >
            <Form.Item
              name="company_name"
              label="Company Name"
              rules={[{ required: true, message: 'Please input company name!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="abbreviation"
              label="Abbreviation"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              name="address"
              label="Address"
            >
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Phone"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Please enter a valid email!' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="facebook"
              label="Facebook URL"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="twitter"
              label="Twitter URL"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="instagram"
              label="Instagram URL"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="linkedin"
              label="LinkedIn URL"
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="youtube"
              label="YouTube URL"
            >
              <Input />
            </Form.Item>

            {isEditing && (
              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    icon={<SaveOutlined />}
                  >
                    Save Changes
                  </Button>
                  <Button 
                    onClick={() => {
                      setIsEditing(false);
                      form.setFieldsValue(settings);
                    }}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            )}
          </Form>
        ) : (
          <p>Loading settings...</p>
        )}
      </Card>
    </div>
  );
};

export default WebSettings;